import { AsyncPipe, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import * as moment from 'moment';
import { Observable, combineLatest, filter, map, take } from 'rxjs';

import { PeriodDateSearchFields, SummaryReportView } from '@app/services/summary.service';
import { ServiceSummaryGroupV2Type } from '@app/services/tickets.service';
import { TurnsSummaryGroupType, UnitSnapshotSummaryType } from '@app/services/turnovers.service';
import { PuSubscribable } from '@app/utils/pu-subscribe';
import { loadAllGeneralRoles } from '@main-application/administration/store/actions/administration.actions';
import {
  selectAllGeneralRoles,
  selectAllUsers,
} from '@main-application/administration/store/selectors/administration.selectors';
import { UserSummaryGroupType } from '@main-application/administration/users/components/user-summary/user-summary.constant';
import {
  BoardUsageSummaryGroupType,
  TurnStepScheduleSummaryGroupType,
} from '@main-application/boards/services/board.service';
import { SortColumnNamePipe } from '@main-application/inspections/components/inspections-wrapper/pipes/sort-column-name.pipe';
import { InspectionsSummaryGroupType } from '@main-application/inspections/services/inspection.service';
import { TimezoneService } from '@main-application/management/pages/system-configuration/components/date-time-configuration/timezone.service';
import {
  FilterByFieldInfoModel,
  PeriodType,
  PeriodTypeExtended,
  ShowByFieldInfoModel,
  SortByInfoModel,
  SummaryDataSource,
  SummaryReportSettingsModel,
  SummaryType,
} from '@main-application/shared/services/summary-report-settings.service';
import {
  addSummaryReportSetting,
  addSummaryReportSettingFailure,
  addSummaryReportSettingSuccess,
  updateSummaryReportSetting,
  updateSummaryReportSettingFailure,
  updateSummaryReportSettingSuccess,
} from '@main-application/store/actions/summary-report-settings.actions';
import { selectCombinedSummarySetting } from '@main-application/store/selectors/summary-report-settings.selectors';
import { selectUserGeneralRole, selectUserPermissionLevel } from '@main-application/store/selectors/user.selectors';
import { PermissionLevelType } from '@shared/enums/permission-level.enum';
import { GeneralRole } from '@shared/interfaces/general-role';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { RestUserModel } from '@shared/interfaces/user.interface';
import { ColorPickerModule } from '@ui-components/components/color-picker/color-picker.module';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';
import { InputErrorModule } from '@ui-components/components/input-error/input-error.module';
import { DateDropdownModule } from '@ui-components/controls/date-dropdown/date-dropdown.module';
import { InputModule } from '@ui-components/controls/input/input.module';
import { InputAreaModule } from '@ui-components/controls/input-area/input-area.module';
import { RadioButtonDropdownModule } from '@ui-components/controls/radio-button-dropdown/radio-button-dropdown.module';
import { UserSelectDropdownModule } from '@ui-components/controls/user-select-dropdown/user-select-dropdown.module';

import { DialogResult } from '../config/dialog-result.enum';

@UntilDestroy()
@Component({
  selector: 'app-summary-storage-modal',
  standalone: true,
  templateUrl: './summary-storage-modal.component.html',
  styleUrls: ['./summary-storage-modal.component.scss'],
  imports: [
    NgIf,
    AsyncPipe,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    RadioButtonDropdownModule,
    UserSelectDropdownModule,
    InputModule,
    InputAreaModule,
    InputErrorModule,
    DateDropdownModule,
    ColorPickerModule,
    MatProgressSpinnerModule,
    SortColumnNamePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SummaryStorageModalComponent extends PuSubscribable implements OnInit {
  public data = inject<{ settings: SummaryReportSettingsModel; saveAsNew: boolean; isFilterSettings?: boolean }>(
    MAT_DIALOG_DATA
  );
  private store = inject(Store);
  private actions$ = inject(Actions);
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private timezoneService = inject(TimezoneService);
  private snackbarService = inject(SnackbarService);
  private dialogRef = inject(MatDialogRef<SummaryStorageModalComponent>);

  public isSaving = false;
  public hasEditPermission = false;
  users$: Observable<RestUserModel[]> = inject(Store).select(selectAllUsers);
  roles$: Observable<IRadioButtonOption<number>[]> = this.store.select(selectAllGeneralRoles).pipe(
    untilDestroyed(this),
    filter((generalRoles: GeneralRole[]) => Array.isArray(generalRoles) && generalRoles?.length > 0),
    map((generalRoles: GeneralRole[]) =>
      generalRoles.map(role => ({
        value: role.id,
        label: role.roleName,
      }))
    )
  );

  configDescription: {
    viewBy?: string;
    periodBy?: {
      periodTitle?: string;
      periodBy?: string;
      startDate?: string;
      endDate?: string;
    };
    periodBySecondary?: {
      periodTitle?: string;
      periodBy?: string;
      startDate?: string;
      endDate?: string;
    };
    showBy?: string;
    filterBy?: string;
    sortBy?: SortByInfoModel;
  } = null;

  private readonly today = this.timezoneService.getCurrentDateOnly();

  form = this.fb.group(
    {
      id: [null],
      name: [null as string, [Validators.maxLength(255)]],
      description: [null as string, [Validators.maxLength(1000)]],
      summaryType: [null as number],
      periodDateSearchFields: [null as number],
      periodType: [null as PeriodType],
      startDate: [null as string],
      endDate: [null as string],
      periodBType: [null as PeriodType | PeriodTypeExtended],
      periodBStartDate: [null as string],
      periodBEndDate: [null as string],
      showBy: [null as ShowByFieldInfoModel[]],
      filterBy: [null as FilterByFieldInfoModel[]],
      dataSource: [null as SummaryDataSource],
      dataSourceModifier: [null as string],
      rolesCanView: [null as number[], [atLeastOneItemRequiredValidator()]],
      rolesCanEdit: [null as number[]],
      reportView: [null as number, [Validators.required]],
      reportLayout: [null as number],
      vsModeEnabled: [null as boolean],
      vsModeComparisonMode: [null as number],
      sortBy: [null as SortByInfoModel[]],
      defaultForRoles: [null as number[]],
      rolesHaveOnDashboard: [null as number[]],
    },
    { validators: defaultRolesSubsetValidator() }
  );

  ngOnInit(): void {
    if (this.data) {
      this.store.dispatch(loadAllGeneralRoles());
      const option = this.data.settings;

      this.form.patchValue({
        id: option.id,
        name: option.name,
        description: option.description,
        summaryType: option.summaryType,
        periodType: option.periodType,
        startDate: option.startDate,
        endDate: option.endDate,
        periodBType: option.periodBType,
        periodBStartDate: option.periodBStartDate,
        periodBEndDate: option.periodBEndDate,
        showBy: option.showBy,
        filterBy: option.filterBy,
        dataSource: option.dataSource,
        dataSourceModifier: option.dataSourceModifier,
        rolesCanView: option.rolesCanView,
        rolesCanEdit: option.rolesCanEdit,
        reportView: option.reportView ?? SummaryReportView.IncompleteByStatus,
        reportLayout: option.reportLayout,
        vsModeEnabled: option.vsModeEnabled,
        vsModeComparisonMode: option.vsModeComparisonMode,
        sortBy: option.sortBy,
        defaultForRoles: option.defaultForRoles,
        rolesHaveOnDashboard: option.rolesHaveOnDashboard,
        periodDateSearchFields: option.periodDateSearchFields,
      });

      const sortBy =
        option.sortBy?.length > 0 && option.reportView > 0 && !!option.sortBy[option.reportView - 1]?.columnName
          ? option.sortBy[option.reportView - 1]
          : null;

      this.configDescription = {
        viewBy: this.getReportViewLabel(option.reportView, option.summaryType),
        periodBy: this.getPeriodByDescription(option.summaryType, option.periodType, option.startDate, option.endDate),
        periodBySecondary: this.getPeriodByDescription(
          option.summaryType,
          option.periodBType,
          option.periodBStartDate,
          option.periodBEndDate
        ),
        showBy: this.getShowByFieldTypeLabels(option.showBy, option.summaryType),
        filterBy: this.getFilterByFieldTypeLabels(option.filterBy, option.summaryType),
        sortBy,
      };

      combineLatest([
        this.store.select(selectAllGeneralRoles).pipe(
          filter(r => r?.length > 0),
          take(1)
        ),
        this.store.select(selectUserGeneralRole).pipe(filter(Boolean), take(1)),
      ])
        .subscribe(([allRoles, userRoleName]) => {
          const matchedUserRole = allRoles.find(r => r.roleName === userRoleName);

          const rolesCanView = this.form.get('rolesCanView')?.value;
          const rolesCanEdit = this.form.get('rolesCanEdit')?.value;

          const patch: Partial<SummaryReportSettingsModel> = {};

          if (!rolesCanView || rolesCanView.length === 0) {
            patch.rolesCanView = allRoles.map(r => r.id);
          }

          if ((!rolesCanEdit || rolesCanEdit.length === 0) && matchedUserRole) {
            patch.rolesCanEdit = [matchedUserRole.id];
          }

          if (Object.keys(patch).length > 0) {
            this.form.patchValue(patch);
            this.cdr.markForCheck();
          }
        })
        .untilDestroyed(this);

      combineLatest([
        this.store.select(selectCombinedSummarySetting(option.summaryType)).pipe(filter(Boolean), take(1)),
        this.store.select(selectUserPermissionLevel).pipe(filter(Boolean), take(1)),
        this.store.select(selectAllGeneralRoles).pipe(
          filter(roles => roles?.length > 0),
          take(1)
        ),
        this.store.select(selectUserGeneralRole).pipe(filter(Boolean), take(1)),
      ])
        .pipe(untilDestroyed(this))
        .subscribe(([summarySettings, permissionLevel, allRoles, roleName]) => {
          const isNew = !summarySettings?.modified?.id || this.data.saveAsNew;
          const isSaveAsNew = this.data.saveAsNew;
          const canEditByPermission = permissionLevel?.includes(PermissionLevelType.AllPermissions);
          const matchedRole = allRoles?.find(r => r?.roleName === roleName);
          const canEditByRole = matchedRole && this.data?.settings?.rolesCanEdit?.includes(matchedRole.id);

          this.hasEditPermission = isNew || isSaveAsNew || canEditByPermission || canEditByRole;
        })
        .untilDestroyed(this);

      this.actions$
        .pipe(
          ofType(
            addSummaryReportSettingSuccess,
            updateSummaryReportSettingSuccess,
            addSummaryReportSettingFailure,
            updateSummaryReportSettingFailure
          ),
          untilDestroyed(this)
        )
        .subscribe(action => {
          if (
            action.type === addSummaryReportSettingSuccess.type ||
            action.type === updateSummaryReportSettingSuccess.type
          ) {
            this.isSaving = false;
            this.dialogRef.close({ result: DialogResult.Success, setting: action.setting });
            this.cdr.markForCheck();
          } else {
            this.isSaving = false;
            this.cdr.markForCheck();
          }
        })
        .untilDestroyed(this);
    }
  }

  cancel() {
    this.dialogRef.close({ result: DialogResult.Fail });
  }

  save() {
    if (this.form.errors?.defaultRolesInvalid) {
      const defaultForRoles = this.form.get('defaultForRoles')?.value || [];
      const viewRoles = this.form.get('rolesCanView')?.value || [];
      const roles = [];
      defaultForRoles.forEach(role => {
        if (!viewRoles.includes(role)) {
          roles.push(role);
        }
      });
      if (roles.length > 0) {
        this.form.get('rolesCanView')?.setValue([...viewRoles, ...roles], { emitEvent: false });
      }
    }

    if (!this.form.value.name || this.form.value.name.trim() === '') {
      this.form.get('name')?.setErrors({ required: true });
      this.form.get('name')?.markAsTouched();
      this.isSaving = false;
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.isSaving = false;
      return;
    }

    this.isSaving = true;

    const optionData = {
      id: this.form.value.id,
      name: this.form.value.name,
      description: this.form.value.description?.trim() === '' ? null : this.form.value.description,
      summaryType: this.form.value.summaryType,
      rolesCanView: this.form.value.rolesCanView,
      rolesCanEdit: this.form.value.rolesCanEdit,
      periodType: this.form.value.periodType,
      startDate: this.form.value.startDate,
      endDate: this.form.value.endDate,
      periodBType: this.form.value.periodBType,
      periodBStartDate: this.form.value.periodBStartDate,
      periodBEndDate: this.form.value.periodBEndDate,
      showBy: this.form.value.showBy,
      filterBy: this.form.value.filterBy,
      dataSource: this.form.value.dataSource,
      dataSourceModifier: this.form.value.dataSourceModifier,
      reportView: this.form.value.reportView,
      reportLayout: this.form.value.reportLayout,
      vsModeEnabled: this.form.value.vsModeEnabled,
      vsModeComparisonMode: this.form.value.vsModeComparisonMode,
      sortBy: this.form.value.sortBy,
      defaultForRoles: this.form.value.defaultForRoles,
      rolesHaveOnDashboard: this.form.value.rolesHaveOnDashboard,
      periodDateSearchFields: this.form.value.periodDateSearchFields,
    };

    this.data.saveAsNew
      ? this.store.dispatch(addSummaryReportSetting({ setting: optionData }))
      : this.store.dispatch(updateSummaryReportSetting({ id: optionData.id, setting: optionData }));
  }

  getDataSourceType(dataSourceType: SummaryDataSource): string {
    switch (dataSourceType) {
      case SummaryDataSource.BB_All:
        return 'Boards (all)';
      case SummaryDataSource.BB_ById:
        return 'Boards (by id)';
      case SummaryDataSource.BB_ByType:
        return 'Boards (by type)';
      case SummaryDataSource.BoardsUsage_All:
        return 'Boards Usage (all)';
      case SummaryDataSource.BoardsUsage_ByID:
        return 'Boards Usage (by id)';
      case SummaryDataSource.Inspection_Standard_All:
        return 'Inspections (standard)';
      case SummaryDataSource.Inspection_Recurring:
        return 'Inspections (repeating)';
      case SummaryDataSource.Service_All:
        return 'Service (all)';
      case SummaryDataSource.Service_OpenTickets:
        return 'Service (open tickets)';
      case SummaryDataSource.Service_CompletedByDate:
        return 'Service (completed by date)';
      case SummaryDataSource.Service_ServiceTickets:
        return 'Service (service tickets)';
      case SummaryDataSource.Turns_All:
        return 'Turns (all)';
      case SummaryDataSource.Turns_ById:
        return 'Turns (by id)';
      case SummaryDataSource.Turns_ByType:
        return 'Turns (by type)';
      case SummaryDataSource.Inspection_Resident_All:
        return 'Inspections (resident)';
      case SummaryDataSource.UserSummary_All:
        return 'Users (all)';
      case SummaryDataSource.UserSummary_ByID:
        return 'Users (by id)';
      default:
        return 'Unknown';
    }
  }

  getReportViewLabel(view: number, summaryType: SummaryType): string | null {
    const key = SummaryReportView[view];
    if (!key) return null;

    const formatted = key
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .trim()
      .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());

    const insert = this.getSummaryItemLabel(summaryType);
    if (!insert) return formatted;

    const parts = formatted.split(' ');
    parts.splice(1, 0, insert);
    return parts.join(' ');
  }

  getSummaryLabel(summaryType: SummaryType): string | null {
    switch (summaryType) {
      case SummaryType.Turns:
        return 'Turnover';
      case SummaryType.Service:
        return 'Service';
      case SummaryType.Inspection:
        return 'Inspections';
      case SummaryType.BB:
        return 'Boards';
      default:
        return null;
    }
  }

  getSummaryItemLabel(summaryType: SummaryType): string | null {
    switch (summaryType) {
      case SummaryType.Turns:
        return 'Turns';
      case SummaryType.Service:
        return 'Tickets';
      case SummaryType.Inspection:
        return 'Inspections';
      case SummaryType.BB:
        return 'Boards';
      case SummaryType.UserSummary:
        return 'Users';
      default:
        return null;
    }
  }

  // todo: need to update to use customFieldId

  getShowByFieldTypeLabels(list: ShowByFieldInfoModel[] = [], summaryType: SummaryType): string {
    const enumType = this.getEnumForSummaryType(summaryType);

    return list
      .map(item => {
        const type = item?.fieldType;
        return type != null ? this.formatEnumKey(enumType[type]) : 'Unknown';
      })
      .join(' → ');
  }

  getFilterByFieldTypeLabels(list: FilterByFieldInfoModel[] | null | undefined = [], summaryType: SummaryType): string {
    if (!Array.isArray(list)) return '';

    const enumType = this.getEnumForSummaryType(summaryType);

    return list
      .map(item => {
        const type = item?.fieldType;
        const name = type != null ? this.formatEnumKey(enumType[type]) : 'Unknown';

        if (!item?.values) return `${name}: (All)`;

        const values = item.values
          .split(',')
          .map(v => v.trim())
          .filter(Boolean);
        const labels = (item.labels || '')
          .split(',')
          .map(l => l.trim())
          .filter(Boolean);

        const valueCount = values.length;
        const visibleLabels = labels.slice(0, 2);
        const remainingCount = valueCount - visibleLabels.length;

        const labelText =
          valueCount === 0
            ? '(All)'
            : remainingCount > 0
            ? `${visibleLabels.join(', ')} & ${remainingCount} more`
            : labels.join(', ');

        return `${name}: ${labelText}`;
      })
      .join('; ');
  }

  getEnumForSummaryType(summaryType: SummaryType): any {
    switch (summaryType) {
      case SummaryType.Service:
      case SummaryType.ServiceList:
        return ServiceSummaryGroupV2Type;
      case SummaryType.Turns:
        return this.form.value.reportView === 3 ? UnitSnapshotSummaryType : TurnsSummaryGroupType;
      case SummaryType.Inspection:
      case SummaryType.InspectionList:
      case SummaryType.ResidentInspectionsList:
        return InspectionsSummaryGroupType;
      case SummaryType.BB:
        return this.form.value.reportView === 5 ? BoardUsageSummaryGroupType : TurnStepScheduleSummaryGroupType;
      case SummaryType.UserSummary:
        return UserSummaryGroupType;
      default:
        return {};
    }
  }

  getPeriodByDescription(
    summaryType: SummaryType,
    periodType: PeriodType | PeriodTypeExtended | null,
    startDate?: string,
    endDate?: string
  ):
    | {
        periodTitle?: string;
        periodBy?: string;
        startDate?: string;
        endDate?: string;
      }
    | undefined {
    if (periodType == null) return undefined;

    const isTurnsSummary = summaryType === SummaryType.Turns;
    const isCompletedTurnsView =
      isTurnsSummary && (this.data?.settings?.reportView ?? 0) > SummaryReportView.CompleteByPeriod;

    if (isTurnsSummary && !isCompletedTurnsView) return undefined;

    const getFilterTitle = () => {
      const periodDateSearchFields = this.form.get('periodDateSearchFields')?.value;
      switch (periodDateSearchFields) {
        case PeriodDateSearchFields.CompletionDateOnly:
          return 'Completion Date';
        case PeriodDateSearchFields.DueDateOnly:
          return 'Due Date';
        case PeriodDateSearchFields.CreatedAtDateOnly:
          return 'Submission Date';
      }
      return 'Any Timeframe';
    };

    const periodTitle = isTurnsSummary
      ? 'Completed'
      : summaryType === SummaryType.InspectionList ||
        summaryType === SummaryType.ResidentInspectionsList ||
        summaryType === SummaryType.ServiceList
      ? getFilterTitle()
      : summaryType === SummaryType.Inspection
      ? 'Due On'
      : summaryType === SummaryType.Service || summaryType === SummaryType.BB
      ? 'Submitted On'
      : undefined;

    const rawPeriod = PeriodType[periodType as PeriodType] ?? PeriodTypeExtended[periodType as PeriodTypeExtended];
    const periodBy = rawPeriod
      ? rawPeriod
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .replace(/([a-zA-Z])([0-9])/g, '$1 $2')
          .replace(/([0-9])([a-zA-Z])/g, '$1 $2')
          .replace(/_/g, ' ')
          .toLowerCase()
          .replace(/\b\w/g, c => c.toUpperCase())
      : undefined;

    const isCustomPeriod = periodType === PeriodType.Custom || periodType === PeriodTypeExtended.Custom;
    const formattedStart = isCustomPeriod && startDate ? this.formatDateMMDDYYYY(startDate) : undefined;

    const formattedEnd = isCustomPeriod && endDate ? this.formatDateMMDDYYYY(endDate) : undefined;

    return {
      periodTitle,
      periodBy,
      startDate: formattedStart,
      endDate: formattedEnd,
    };
  }

  formatDateMMDDYYYY(date: string): string {
    return moment(date).format('MM/DD/YYYY');
  }

  formatEnumKey(key?: string): string {
    if (!key) return;
    return key.replace(/([A-Z])/g, ' $1').trim();
  }

  public shouldDisplayPeriod(): boolean {
    const isUsersSummary = this.data.settings.summaryType === SummaryType.UserSummary;
    const isTurnsSummary = this.data.settings.summaryType === SummaryType.Turns;

    if (isUsersSummary) return false;
    if (isTurnsSummary) return this.data.settings.reportView > 3;
    return true;
  }
}

export function atLeastOneItemRequiredValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    return Array.isArray(value) && value.length > 0 ? null : { required: true };
  };
}

export function defaultRolesSubsetValidator(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const viewRoles = (group.get('rolesCanView')?.value as number[]) || [];
    const editRoles = (group.get('rolesCanEdit')?.value as number[]) || [];
    const defaultRoles = (group.get('defaultForRoles')?.value as number[]) || [];

    const invalidDefaults = defaultRoles.filter(role => !viewRoles.includes(role) && !editRoles.includes(role));

    return invalidDefaults.length > 0 ? { defaultRolesInvalid: true } : null;
  };
}
